<template>
  <div class="p-home">
    <div class="p-home__search">
      <h2 ref="heading" class="p-home__title" data-testid="homepage-header">
        {{ searchHeading }}
      </h2>
      <div class="p-home__search-box">
        <RadAdvancedSearchBox
          ref="searchBox"
          :display-chips="false"
          :autocomplete-values="suggestions"
          :autocomplete-options="{
            minChars: 1,
            allowAnyInput: true,
            includeQueryDuplicatesMatchedValue: true,
            addSuffix: addSuffix,
            additionalNormalization: additionalNormalization,
            sortResults: (a, b) => {
              if (useAlphabeticTypeaheadSort == false) {
                return 0;
              }
              return a.match_string.localeCompare(b.match_string);
            }
          }"
          :intercept-autocomplete-event="true"
          @get-autocomplete-values="fetchSuggestions"
          @autocomplete-entry="autocompleteEntry"
          @update-search="triggerSearch"
        >
          <SearchSuggestionLinks class="p-home__suggested-searches" />
        </RadAdvancedSearchBox>
      </div>
    </div>
    <div class="p-home__body-content">
      <HomepageVisualization />
    </div>
    <Module :modifiers="['collection-policy']">
      <template #splitLeft>
        <Heading :level="3">
          {{ collectionPolicyHeading }}
        </Heading>
      </template>
      <template #splitRight>
        <p>{{ collectionPolicyText }}</p>
        <ul>
          <li>
            <GuiLink
              href="https://media.getty.edu/Text/00ae0cba-827f-5f1a-877e-86468937f24a.pdf"
              target="_blank"
            >
              {{ collectionPolicyLink }}
            </GuiLink>
          </li>
          <li>
            <GuiLink
              href="https://www.getty.edu/museum/deaccessioning/"
              target="_blank"
            >
              {{ deaccessioningLink }}
            </GuiLink>
          </li>
        </ul>
      </template>
    </Module>
    <div class="p-home__explore-links">
      <LinkGrid
        ref="exploreLinks"
        :link-list-primary="{ items: exploreLinksList }"
      >
        {{ exploreLinksHeading }}
      </LinkGrid>
    </div>
  </div>
</template>

<script>
import {
  LinkGrid,
  RadAdvancedSearchBox,
  Heading,
  GuiLink,
  Module,
  useLocalization
} from "@thegetty-private/getty-ui";
import HomepageVisualization from "@/components/organisms/homepageVisualization/index.vue";
import SearchSuggestionLinks from "@/components/atoms/searchSuggestionLinks/index.vue";
import display from "@data/display";
import explore_links from "@data/explore_links";
import {
  searchService,
  AutoCompleteMixin
} from "@thegetty-private/rad-app-utils";
import * as configHelper from "@/helpers/configHelper";
import * as autocompleteHelpers from "@/helpers/autocomplete";
import { useHead } from "@unhead/vue";

export default {
  name: "HomePage",
  components: {
    HomepageVisualization,
    LinkGrid,
    RadAdvancedSearchBox,
    SearchSuggestionLinks,
    Heading,
    GuiLink,
    Module
  },
  mixins: [AutoCompleteMixin],
  inject: ["updatePageTitle"],
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * The translated heading for the search box
       */
      searchHeading: this.translatePart("homepage.search_heading"),
      /* The suggestions from the autocomplete endpoint are stored here and passed to radAdvancedSearchBox for
       * rendering
       */
      suggestions: [],
      /**
       * The translated heading for the explore links
       */
      exploreLinksHeading: this.translatePart("explore_links.heading"),
      /**
       * the page title
       */
      pageTitle: this.translatePart("homepage.page_title"),
      /**
       * Deaccession policy section heading
       */
      collectionPolicyHeading: this.translatePart(
        "homepage.collection_policy_heading"
      ),
      /**
       * Deaccession policy section text
       */
      collectionPolicyText: this.translatePart(
        "homepage.collection_policy_text"
      ),
      /**
       * Deaccession policy section link text
       */
      collectionPolicyLink: this.translatePart(
        "homepage.collection_policy_link"
      ),
      /**
       * Deaccessioning page link text
       */
      deaccessioningLink: this.translatePart("homepage.deaccessioning_link")
    };
  },
  computed: {
    /**
     * Whether to sort the autocomplete by relevancy or alphabetically
     *
     * @returns {boolean}
     */
    useAlphabeticTypeaheadSort() {
      return configHelper.useAlphabeticTypeaheadSort();
    },
    /**
     * Gets the the translated list of links for the explore LinkGrid component
     *
     * @returns {array} of link objects
     */
    exploreLinksList() {
      let exploreLinksList = explore_links.map((link) => {
        link.text = this.translatePart(link.text);
        link.underline = true;
        return link;
      });
      return exploreLinksList;
    }
  },
  mounted() {
    this.updatePageTitle(this.pageTitle);

    useHead({
      title: this.pageTitle
    });
  },
  methods: {
    /**
     * Inserts <i> tags into the displayed suggestion string to show the type
     * that the match is (e.g. (Title), (Person))
     *
     * @param {String} text - The current (match) string
     * @param {Object} suggestionObject - The matched result's details
     *
     * @returns {String}
     */
    addSuffix(text, suggestionObject) {
      return autocompleteHelpers.addSuffix(
        text,
        suggestionObject,
        this.translatePart
      );
    },
    /** Event to be triggered instead of an autocomplete search event
     *
     * @param {Object} event - details of the selected suggestion that is passed
     * through the event
     */
    autocompleteEntry(event) {
      return autocompleteHelpers.autocompleteEntry(event, this.$router);
    },
    triggerSearch(event) {
      let params = {
        from: 0,
        size: display.PAGE_SIZE,
        view: display.VIEW
      };

      searchService.handleSearchEvent(event, this.$router, params, false);
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
